"use client";

import React from 'react';
import { MessageCitation } from '@/types/chat';
import { ChatCanvasHeader } from './ChatCanvasHeader';
import { ChatCanvasEmptyState } from './ChatCanvasEmptyState';
import { CanvasItem } from './CanvasItem';
import { Accordion } from '../../components/motion-primitives/accordion';
import { normalizeUrl } from '@/lib/urlNormalize';
import { useCitationStore } from '@/stores/citationStore';

interface ChatCanvasProps {
  isOpen: boolean;
  onClose: () => void;
  items: MessageCitation[];
}

export const ChatCanvas: React.FC<ChatCanvasProps> = ({ isOpen, onClose, items }) => {
  const { activeUrl } = useCitationStore();
  const [expandedValue, setExpandedValue] = React.useState<React.Key | null>(null);

  // Sync accordion expansion with activeUrl from citation store (hover state)
  React.useEffect(() => {
    if (activeUrl) {
      setExpandedValue(activeUrl);
    } else {
      // Clear expanded state when hover ends
      setExpandedValue(null);
    }
  }, [activeUrl]);

  if (!isOpen) return null;

  const hasItems = items && items.length > 0;

  const getDisplayUrl = (rawUrl: string) => {
    if (!rawUrl) return '';
    // Trim whitespace and strip common trailing punctuation/brackets that often
    // get included when copying links from markdown or text (e.g. trailing ')').
    return rawUrl
      .trim()
      .replace(/[)\]\}>,.;:]+$/g, '');
  };

  return (
    <aside
      className="hidden lg:flex flex-col w-80 border-l border-gray-200 bg-white dark:bg-neutral-900 dark:border-neutral-800"
      data-scroll-proxy-blocker="true"
    >
      <ChatCanvasHeader itemCount={items.length} onClose={onClose} />

      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {!hasItems && <ChatCanvasEmptyState />}

        {hasItems && (
          <Accordion
            className="space-y-2"
            expandedValue={expandedValue}
            onValueChange={setExpandedValue}
          >
            {items.map((item, index) => {
              const displayUrl = getDisplayUrl(item.url);
              const urlToUse = displayUrl || item.url;
              const normalizedUrl = normalizeUrl(urlToUse);

              return (
                <CanvasItem
                  key={item.id}
                  item={{ ...item, url: urlToUse }}
                  index={index}
                  normalizedUrl={normalizedUrl}
                />
              );
            })}
          </Accordion>
        )}
      </div>
    </aside>
  );
};
